import { useState } from "react";
import { useBaysByGreenhouseId } from "../../hooks/useGreenhouseBays";
import { ViewBayModal } from "./ViewBayModal";
import { CreateGreenhouseBayDialog } from "./CreateGreenhouseBayDialog";
import type { Greenhouse } from "../../services/greenhousesApi";
import type { GreenhouseBay } from "../../services/greenhouseBaysApi";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ViewGreenhouseDialogProps {
  greenhouse: Greenhouse;
}

export const ViewGreenhouseDialog = ({
  greenhouse,
}: ViewGreenhouseDialogProps) => {
  const [open, setOpen] = useState(false);
  const [selectedBay, setSelectedBay] = useState<GreenhouseBay | null>(
    null
  );

  const { data: bays = [], isLoading } = useBaysByGreenhouseId(
    greenhouse.id
  );

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2">
            <Eye className="w-4 h-4" />
            View
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{greenhouse.greenhouseName}</DialogTitle>
            <DialogDescription>
              {greenhouse.location} • {greenhouse.sizeSquareMeters}m² •
              Manager: {greenhouse.managerUserName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {greenhouse.description && (
              <div className="text-sm text-muted-foreground">
                {greenhouse.description}
              </div>
            )}

            <div className="flex justify-between items-center">
              <h3 className="font-semibold">Bays ({bays.length})</h3>
              <CreateGreenhouseBayDialog
                greenhouseId={greenhouse.id}
                greenhouseSize={greenhouse.sizeSquareMeters}
                existingBays={bays}
              />
            </div>

            {isLoading ? (
              <LoadingAnimation text="Loading bays..." />
            ) : bays.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No bays created yet
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {bays.map((bay) => (
                  <button
                    key={bay.id}
                    onClick={() => setSelectedBay(bay)}
                    className="p-4 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer text-left"
                  >
                    <h4 className="font-semibold text-sm mb-2">
                      {bay.bayName}
                    </h4>
                    <div className="space-y-1 text-xs text-muted-foreground">
                      <p>Rows: {bay.bedCount}</p>
                      <p>Size: {bay.sizeSquareMeters}m²</p>
                      <p>Capacity: {bay.trayCapacity}</p>
                    </div>
                    <div className="mt-2">
                      <Badge
                        variant={bay.isActive ? "default" : "destructive"}
                      >
                        {bay.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {selectedBay && (
        <ViewBayModal
          bay={selectedBay}
          open={!!selectedBay}
          onOpenChange={(isOpen) => !isOpen && setSelectedBay(null)}
        />
      )}
    </>
  );
};
